import { memo } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import {
  AlertCircle,
  Calendar,
  ExternalLink,
  GithubIcon,
  Lock,
  MessageSquare,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { AssignCombobox } from '@/components/compliance/AssignCombobox'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { SlaIndicator } from '@/components/compliance/SlaBadge'
import {
  DEFAULT_CARD_PROPERTIES,
  type CardPropertyToggles,
  type ViewMode,
} from './TasksViewState'
import { isDoneStatus } from './taskColumns.config'
import type { ActionItem } from './types'

const AGENT_STATUS_CONFIG: Record<'QUEUED' | 'RUNNING', { label: string; dotClass: string; textClass: string }> = {
  QUEUED: { label: 'Queued', dotClass: 'bg-icon-muted', textClass: 'text-text-placeholder' },
  RUNNING: { label: 'Running', dotClass: 'bg-primary animate-pulse', textClass: 'text-status-blue' },
}

const PRIORITY_LABEL: Record<string, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

export const PRIORITY_STYLE: Record<string, string> = {
  HIGH: 'text-status-orange-emphasis',
  MEDIUM: 'text-status-amber-emphasis',
  LOW: 'text-muted-foreground',
}

export function PrioritySignalIcon({
  priority,
  className,
}: {
  priority: string
  className?: string
}) {
  const activeBars =
    priority === 'HIGH' ? 3 : priority === 'MEDIUM' ? 2 : priority === 'LOW' ? 1 : 0

  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='currentColor'
      role='img'
      aria-label={`${PRIORITY_LABEL[priority] ?? 'No'} Priority`}
      focusable='false'
      className={cn('h-3.5 w-3.5', className)}
      xmlns='http://www.w3.org/2000/svg'
    >
      <rect x='1.5' y='8' width='3' height='6' rx='1' opacity={activeBars >= 1 ? 1 : 0.25} />
      <rect x='6.5' y='5' width='3' height='9' rx='1' opacity={activeBars >= 2 ? 1 : 0.25} />
      <rect x='11.5' y='2' width='3' height='12' rx='1' opacity={activeBars >= 3 ? 1 : 0.25} />
    </svg>
  )
}

const CATEGORY_SHORT: Record<string, string> = {
  manual: 'MAN',
  remediation: 'REM',
  evidence: 'TST',
  agent_escalation: 'ESC',
  policy_approval: 'POL',
  consistency_gap: 'GAP',
  evidence_expired: 'EVD',
  scan_finding: 'SCN',
}

const FREQUENCY_LABEL: Record<string, string> = {
  ONCE: 'One-time',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-annual',
  ANNUAL: 'Annual',
}

export function shortCode(item: ActionItem): string {
  // Prefer the real workspace task code (e.g. TASK-042, WOX-34) when the
  // backend provides one. Fall back to a synthesized {CAT}-{last4} label.
  const meta = item.meta as
    | { testCode?: string | null; displayCode?: string | null }
    | undefined
  if (meta?.displayCode) return meta.displayCode
  if (meta?.testCode) return meta.testCode
  const prefix = CATEGORY_SHORT[item.category] ?? 'TSK'
  const suffix = item.id.slice(-4).toUpperCase()
  return `${prefix}-${suffix}`
}

function formatDueDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

interface TaskCardProps {
  item: ActionItem
  onClick: (item: ActionItem) => void
  onReassign?: (item: ActionItem, userId: string) => Promise<void> | void
  selected?: boolean
  onSelectToggle?: (item: ActionItem) => void
  isOverlay?: boolean
  cardProperties?: CardPropertyToggles
  viewMode?: ViewMode
}

function TaskCardComponent({
  item,
  onClick,
  onReassign,
  selected = false,
  onSelectToggle,
  isOverlay,
  cardProperties = DEFAULT_CARD_PROPERTIES,
  viewMode = 'comfortable',
}: TaskCardProps) {
  const draggable = useDraggable({
    id: `${item.category}:${item.id}`,
    data: { item },
    disabled: item.readOnly,
  })

  const { attributes, listeners, setNodeRef, transform, isDragging } = draggable

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging && !isOverlay ? 0 : 1,
  }

  const agentStatusCfg =
    item.isAgent &&
    item.agentTaskStatus &&
    (item.agentTaskStatus === 'QUEUED' || item.agentTaskStatus === 'RUNNING')
      ? AGENT_STATUS_CONFIG[item.agentTaskStatus]
      : null

  const now = new Date()
  const isOverdue =
    item.dueDate &&
    new Date(item.dueDate) < now &&
    !isDoneStatus(item.taskStatus)

  const readOnly = item.readOnly
  const runProgress = item.meta?.runProgress
  const procedureFailed =
    item.meta?.runStatus === 'FAILED' ||
    (typeof runProgress === 'object' &&
      runProgress !== null &&
      'runStatus' in runProgress &&
      runProgress.runStatus === 'FAILED')

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onClick(item)}
      className={cn(
        'group relative cursor-pointer rounded-md border border-border bg-card text-left transition-shadow',
        viewMode === 'compact' ? 'p-2.5' : 'p-3',
        'hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
        isOverlay && 'rotate-2 shadow-[0_8px_20px_rgba(0,0,0,0.12)]',
        readOnly && 'cursor-default',
        selected && 'border-ring shadow-[0_0_0_1px_rgba(63,95,206,0.2)]'
      )}
    >
      {!isOverlay && onSelectToggle && !readOnly && (
        <div className='absolute top-2 right-2 z-10'>
          <Checkbox
            checked={selected}
            onCheckedChange={() => onSelectToggle(item)}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className={cn(
              'h-4 w-4 rounded-sm border-border-divider bg-card',
              selected
                ? 'opacity-100'
                : 'opacity-0 transition-opacity group-hover:opacity-100'
            )}
            aria-label='Select task'
          />
        </div>
      )}

      {/* Top row: short ID + open-request badge + lock */}
      <div className='mb-1 flex items-center gap-2'>
        <span className='font-mono text-[10px] tracking-wide text-text-placeholder'>
          {shortCode(item)}
        </span>
        {(item.openRequestCount ?? 0) > 0 && (
          <span
            className='inline-flex items-center gap-1 rounded-sm border border-status-orange/30 bg-status-orange-subtle px-1.5 py-0.5 text-[10px] font-medium text-status-orange'
            title={`${item.openRequestCount} open auditor request${
              item.openRequestCount === 1 ? '' : 's'
            }`}
          >
            <MessageSquare className='h-2.5 w-2.5' />
            {item.openRequestCount}
          </span>
        )}
        {/* #1418 — consolidated state chips: escalation / policy approval
            render on the ONE test card instead of separate ESC/POL cards. */}
        {typeof item.meta?.agentEscalationReason === 'string' &&
          item.meta.agentEscalationReason &&
          !isDoneStatus(item.taskStatus) && (
            <span
              className='inline-flex items-center gap-1 rounded-[4px] border border-status-orange/30 bg-status-orange-bg px-1.5 py-0.5 text-[10px] font-medium text-status-orange'
              title={item.meta.agentEscalationReason}
            >
              <AlertCircle className='h-2.5 w-2.5' />
              Vera needs your help
            </span>
          )}
        {item.meta?.awaitingPolicyApproval === true &&
          !isDoneStatus(item.taskStatus) && (
            <span
              className='inline-flex items-center gap-1 rounded-[4px] border border-status-blue/30 bg-status-blue-bg px-1.5 py-0.5 text-[10px] font-medium text-status-blue'
              title='The policy behind this test is awaiting approval'
            >
              Awaiting approval
            </span>
          )}
        {procedureFailed && !isDoneStatus(item.taskStatus) && (
          <span
            className='inline-flex items-center gap-1 rounded-sm border border-status-red/30 bg-status-red-bg px-1.5 py-0.5 text-[10px] font-medium text-status-red'
            title='The procedure run stopped on an error'
          >
            <AlertCircle className='h-2.5 w-2.5' />
            Procedure failed
          </span>
        )}
        {readOnly && (
          <Lock
            className='ml-auto h-3 w-3 text-text-placeholder'
            aria-label='Read-only'
          />
        )}
      </div>

      {/* Title */}
      <div
        className={cn(
          'font-body line-clamp-2 leading-snug font-medium text-text-secondary',
          viewMode === 'compact' ? 'text-[12px]' : 'text-[13px]'
        )}
      >
        {item.title}
      </div>

      {viewMode !== 'compact' &&
      cardProperties.description &&
      item.description ? (
        <div className='mt-1 line-clamp-2 text-[11px] leading-5 text-text-subtle'>
          {item.description}
        </div>
      ) : null}

      {item.category === 'evidence' &&
      viewMode !== 'compact' &&
      item.meta?.frequency &&
      typeof item.meta.frequency === 'string' &&
      item.meta.frequency !== 'ONCE' ? (
        <div className='mt-1.5 text-[10px] text-text-placeholder'>
          {FREQUENCY_LABEL[item.meta.frequency] ?? item.meta.frequency}
        </div>
      ) : null}

      {/* Bottom row: priority chip + assignee + due date */}
      <div
        className={cn(
          'flex items-center gap-2',
          viewMode === 'compact' ? 'mt-2.5' : 'mt-3'
        )}
      >
        {/* Priority chip — subtle, linear-style */}
        {cardProperties.priority ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium',
              PRIORITY_STYLE[item.priority] ?? 'text-muted-foreground'
            )}
          >
            <PrioritySignalIcon priority={item.priority} />
            {PRIORITY_LABEL[item.priority] ?? 'No priority'}
          </span>
        ) : null}

        {cardProperties.dueDate && item.dueDate && (
          <div
            className={cn(
              'flex items-center gap-0.5 text-[10px]',
              isOverdue ? 'font-semibold text-status-red' : 'text-text-subtle'
            )}
          >
            {isOverdue ? (
              <AlertCircle className='h-2.5 w-2.5' />
            ) : (
              <Calendar className='h-2.5 w-2.5' />
            )}
            {formatDueDate(item.dueDate)}
          </div>
        )}

        <SlaIndicator
          aggregate={item.slaAggregate}
          items={item.slaItems}
          compact
        />

        {typeof item.meta?.ticketUrl === 'string' && (
          <a
            href={item.meta.ticketUrl as string}
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className='inline-flex items-center justify-center rounded-sm text-text-subtle transition-colors hover:text-text-secondary'
            aria-label='Open external ticket'
          >
            {(item.meta.ticketUrl as string).includes('github.com') ? (
              <span className='flex h-4 w-4 items-center justify-center rounded-full bg-primary'>
                <GithubIcon className='h-2.5 w-2.5 text-primary-foreground' />
              </span>
            ) : (
              <ExternalLink className='h-3 w-3' />
            )}
          </a>
        )}

        {/* Agent task status indicator */}
        {item.isAgent && agentStatusCfg && (
          <span className='ml-auto inline-flex items-center gap-1'>
            <span className={cn('h-1.5 w-1.5 rounded-full', agentStatusCfg.dotClass)} />
            <span className={cn('text-[10px] font-medium', agentStatusCfg.textClass)}>
              {agentStatusCfg.label}
            </span>
          </span>
        )}

        <div
          className={cn(agentStatusCfg ? 'ml-2' : 'ml-auto')}
          onPointerDown={(e) => {
            // Prevent @dnd-kit from starting a drag when the user is
            // just trying to click the avatar to reassign.
            e.stopPropagation()
          }}
          onClick={(e) => {
            // AssignCombobox renders its popover in a React portal, so
            // clicks inside it still bubble through the React tree up
            // to the card — which would navigate to the detail page.
            // Swallow click propagation at this boundary.
            e.stopPropagation()
          }}
        >
          {cardProperties.assignee && onReassign && !readOnly ? (
            <AssignCombobox
              contentWidth={260}
              showHoverCard={false}
              currentAssigneeId={item.assignedToId ?? null}
              assignee={
                item.assignedTo
                  ? {
                      id: item.assignedTo.id,
                      name: item.assignedTo.name,
                      email: null,
                      image: item.assignedTo.image ?? null,
                    }
                  : null
              }
              onValueChange={(userId) => {
                if (userId && userId !== item.assignedToId) {
                  void onReassign(item, userId)
                }
              }}
            >
              <button
                type='button'
                className='flex items-center justify-center rounded-full transition-transform hover:scale-110'
                aria-label='Assign task'
              >
                {item.assignedTo ? (
                  <MemberAvatar
                    person={{ ...item.assignedTo, isAgent: item.isAgent }}
                    size='sm'
                  />
                ) : (
                  <div className='h-5 w-5 rounded-full border border-dashed border-border-divider' />
                )}
              </button>
            </AssignCombobox>
          ) : cardProperties.assignee && item.assignedTo ? (
            <MemberAvatar
              person={{ ...item.assignedTo, isAgent: item.isAgent }}
              size='sm'
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

// Memoized: with 100s of cards on the board, skip re-rendering cards whose
// props are unchanged (selection toggles, search keystrokes, agent polling).
// Callbacks from the parent are useCallback-stable; `item` keeps a stable
// identity unless the underlying test data actually changes.
export const TaskCard = memo(TaskCardComponent)
